import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Target, Zap, DollarSign } from 'lucide-react';

const OpportunitiesList = ({ opportunities, overview }) => {
  const [expandedOpps, setExpandedOpps] = useState(new Set([0])); // First one expanded by default

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedOpps);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedOpps(newExpanded);
  };

  const ImpactBadge = ({ impact }) => {
    const colors = {
      'HIGH': 'bg-red-100 text-red-800 border-red-300',
      'MEDIUM': 'bg-amber-100 text-amber-800 border-amber-300',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };

    return (
      <span className={`badge ${colors[impact]}`}>
        {impact} Impact
      </span>
    );
  };

  const EffortBadge = ({ effort }) => {
    const colors = {
      'LOW': 'bg-green-100 text-green-800 border-green-300',
      'MEDIUM': 'bg-amber-100 text-amber-800 border-amber-300',
      'HIGH': 'bg-red-100 text-red-800 border-red-300'
    };

    return (
      <span className={`badge ${colors[effort]}`}>
        {effort} Effort
      </span>
    );
  };
  
  if (!opportunities) {
  return <div className="loading-state">Loading...</div>;
}

if (!opportunities.summary) {
  return <div className="loading-state">No opportunity data available.</div>;
}

  return (
    <div className="opportunities-container">
      {/* Summary Header */}
      <div className="opportunities-summary">
        <div className="summary-card">
          <DollarSign className="summary-icon" />
          <div className="summary-content">
            <div className="summary-value">
              £{opportunities.summary.total_annual_value.toLocaleString()}
            </div>
            <div className="summary-label">Total Annual Opportunity</div>
          </div>
        </div>

        <div className="summary-card">
          <Target className="summary-icon" />
          <div className="summary-content">
            <div className="summary-value">
              {opportunities.summary.total_opportunities}
            </div>
            <div className="summary-label">Opportunities Identified</div>
          </div>
        </div>

        <div className="summary-card">
          <Zap className="summary-icon" />
          <div className="summary-content">
            <div className="summary-value">
              {opportunities.summary.low_effort_count}
            </div>
            <div className="summary-label">Quick Wins (Low Effort)</div>
          </div>
        </div>
      </div>

      {/* Opportunities List */}
      <div className="opportunities-list">
        <div className="list-header">
          <h2>Ranked by Financial Impact</h2>
          <p>Click each opportunity to see detailed actions</p>
        </div>

        {opportunities.opportunities.map((opp, index) => {
          const isExpanded = expandedOpps.has(index);

          return (
            <div 
              key={opp.id}
              className={`opportunity-card ${isExpanded ? 'expanded' : ''}`}
            >
              <div 
                className="opportunity-header"
                onClick={() => toggleExpanded(index)}
              >
                <div className="opp-rank">#{index + 1}</div>
                
                <div className="opp-title-section">
                  <h3>{opp.title}</h3>
                  <div className="opp-badges">
                    <ImpactBadge impact={opp.impact} />
                    <EffortBadge effort={opp.effort} />
                    <span className="badge type-badge">{opp.type}</span>
                  </div>
                </div>

                <div className="opp-value-section">
                  <div className="annual-value">
                    £{opp.annual_opportunity.toLocaleString()}
                    <span className="period">/year</span>
                  </div>
                  <div className="monthly-value">
                    £{opp.monthly_opportunity.toLocaleString()}/month
                  </div>
                </div>

                <button className="expand-button">
                  {isExpanded ? <ChevronUp /> : <ChevronDown />}
                </button>
              </div>

              {isExpanded && (
                <div className="opportunity-details">
                  <div className="details-grid">
                    <div className="detail-section">
                      <h4>Current State</h4>
                      <div className="metric-display">
                        <span className="metric-label">Current:</span>
                        <span className="metric-value">
                          {typeof opp.current === 'number' ? 
                            `£${opp.current.toFixed(2)}` : 
                            opp.current
                          }
                        </span>
                      </div>
                      <div className="metric-display">
                        <span className="metric-label">Target:</span>
                        <span className="metric-value">
                          {typeof opp.target === 'number' ? 
                            `£${opp.target.toFixed(2)}` : 
                            opp.target
                          }
                        </span>
                      </div>
                      <div className="metric-display highlight">
                        <span className="metric-label">Gap:</span>
                        <span className="metric-value">
                          {typeof opp.gap === 'number' ? 
                            `£${opp.gap.toFixed(2)}` : 
                            opp.gap
                          }
                        </span>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h4>What This Means</h4>
                      <p className="description">{opp.description}</p>
                    </div>
                  </div>

                  <div className="actions-section">
                    <h4>Recommended Actions</h4>
                    <ul className="actions-list">
                      {opp.actions.map((action, actionIndex) => (
                        <li key={actionIndex} className="action-item">
                          <span className="action-bullet">→</span>
                          <span className="action-text">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Implementation Timeline */}
                  <div className="timeline-section">
                    <h4>Implementation Timeline</h4>
                    <div className="timeline">
                      <div className="timeline-step">
                        <div className="step-number">1</div>
                        <div className="step-content">
                          <strong>Week 1-2:</strong> Analysis & Planning
                        </div>
                      </div>
                      <div className="timeline-step">
                        <div className="step-number">2</div>
                        <div className="step-content">
                          <strong>Week 3-4:</strong> Implementation
                        </div>
                      </div>
                      <div className="timeline-step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                          <strong>Week 5-8:</strong> Monitor & Optimise
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Call to Action */}
      <div className="cta-section">
        <h3>Ready to Capture These Opportunities?</h3>
        <p>
          Use the <strong>Scenario Builder</strong> tab to model exactly how these 
          improvements would impact your bottom line.
        </p>
        <button className="cta-button">
          Build Your Strategy →
        </button>
      </div>
    </div>
  );
};

export default OpportunitiesList;